import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as SupabaseUser, AuthError, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  email_verified: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;  // 💬 SWE-1: usePayment에서 access_token 필요
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  display_name: string;
  phone?: string;
  birthdate?: string;
  terms_agreed: boolean;
  privacy_agreed: boolean;
  marketing_agreed?: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function mapSupabaseUser(supabaseUser: SupabaseUser): User {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    display_name: supabaseUser.user_metadata?.display_name || supabaseUser.user_metadata?.full_name || null,
    avatar_url: supabaseUser.user_metadata?.avatar_url || null,
    email_verified: supabaseUser.email_confirmed_at !== null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);  // 💬 SWE-1: 세션 상태 추가
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 현재 세션 확인
    const initAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.user) {
          setUser(mapSupabaseUser(currentSession.user));
          setSession(currentSession);  // 세션 저장
        }
      } catch (error) {
        console.error('Auth init error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Auth 상태 변경 구독
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, currentSession) => {
        if (currentSession?.user) {
          setUser(mapSupabaseUser(currentSession.user));
          setSession(currentSession);  // 세션 저장
        } else {
          setUser(null);
          setSession(null);
        }
        setIsLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw new Error(getErrorMessage(error));
    }
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw new Error(getErrorMessage(error));
    }
    setUser(null);
  };

  const register = async (data: RegisterData) => {
    const { error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          display_name: data.display_name,
          phone: data.phone,
          birthdate: data.birthdate,
          terms_agreed: data.terms_agreed,
          privacy_agreed: data.privacy_agreed,
          marketing_agreed: data.marketing_agreed,
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      throw new Error(getErrorMessage(error));
    }
    // 이메일 인증 필요 - 자동 로그인하지 않음
  };

  const resendVerificationEmail = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      throw new Error(getErrorMessage(error));
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      throw new Error(getErrorMessage(error));
    }
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      throw new Error(getErrorMessage(error));
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, login, logout, register, resendVerificationEmail, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function getErrorMessage(error: AuthError): string {
  switch (error.message) {
    case 'Invalid login credentials':
      return '이메일 또는 비밀번호가 올바르지 않습니다';
    case 'Email not confirmed':
      return '이메일 인증이 필요합니다. 메일함을 확인해주세요';
    case 'User already registered':
      return '이미 등록된 이메일입니다';
    case 'Password should be at least 6 characters':
      return '비밀번호는 최소 6자 이상이어야 합니다';
    default:
      return error.message || '오류가 발생했습니다';
  }
}
